import './styles/index.css'
import 'vue-final-modal/style.css'
import 'vue-tel-input/vue-tel-input.css'
import 'media-chrome'
import 'media-chrome/menu'
import 'vue3-carousel/dist/carousel.css'
import VueSocketIOExt from 'vue-socket.io-extended'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueTelInput from 'vue-tel-input'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'
import { listenForSessionInvalidation } from '@/services/session-cleanup'
import VueCountdown from '@chenfengyuan/vue-countdown'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import GoogleLogin from 'vue3-google-login'
import { createVfm } from 'vue-final-modal'
import PrimeVue from 'primevue/config'
import Lara from '@primevue/themes/lara'
import ToastService from 'primevue/toastservice'
import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import VueSweetalert2 from 'vue-sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import 'altcha'
import {
  GoogleLoginConfig,
  queryClientConfig,
  telInputGlobalOptions
} from '@/constants'
import { createGtm } from '@gtm-support/vue-gtm'
import VueDragscroll from 'vue-dragscroll'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import VueAwesomePaginate from 'vue-awesome-paginate'
import 'vue-awesome-paginate/dist/style.css'
import Tooltip from 'primevue/tooltip'
import Vue3Lottie from 'vue3-lottie'
import App from './App.vue'
import router from '@/router'
import { AppLink } from './components'
import { getSocket } from './services/socket'
import { initSentry } from './services/sentry'
const intervalMS = 60 * 1000 // Changed from 1 hour to 1 minute to match your cache time

useRegisterSW({
  onRegistered(r) {
    if (r) {
      setInterval(() => {
        console.log('Checking for SW updates...')
        r.update()
      }, intervalMS)
    }
  },
  onRegisterError(error) {
    console.error('SW registration error', error)
  },
  onOfflineReady() {
    console.log('App ready to work offline')
  },
  immediate: true
})

const app = createApp(App)
const bootstrap = () => {
  const socket = getSocket()

  if (socket) {
    app.use(VueSocketIOExt, socket)
  }

  const vueFinalModal = createVfm()
  const isConsentGiven = localStorage.getItem('cookie-consent') === 'accepted'

  app.use(FloatingVue)

  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)

  app.directive('tooltip', Tooltip)
  app.component(VueCountdown.name || 'VueCountDown', VueCountdown)
  app.component('AppLink', AppLink)
  app.use(pinia)
  app.use(router)
  const queryClient = new QueryClient('queryClientConfig' in queryClientConfig ? queryClientConfig.queryClientConfig : undefined)
  app.use(VueQueryPlugin, { ...queryClientConfig, queryClient })
  listenForSessionInvalidation(queryClient, router)
  // @ts-ignore
  app.use(VueTelInput, telInputGlobalOptions)
  app.use(VueSweetalert2)
  app.use(PrimeVue, {
    theme: {
      preset: Lara,
      options: {
        // this is the easiest way of disabling automatic dark_mode of `primevue`
        darkModeSelector: '.something_else'
      }
    }
  })
  app.use(ToastService)
  if (GoogleLoginConfig.clientId) {
    app.use(GoogleLogin, GoogleLoginConfig)
  }
  app.use(vueFinalModal)

  //vue cropper component
  // @ts-ignore
  app.use(VueCropper)

  const gtmId = import.meta.env.VITE_GTAG_ID
  if (/^GTM-[0-9A-Z]+$/.test(gtmId)) {
    app.use(
      createGtm({
        id: gtmId,
        defer: true,
        compatibility: false,
        enabled: isConsentGiven,
        debug: true,
        loadScript: true,
        vueRouter: router,
        trackOnNextTick: false
      })
    )
  } else {
    console.warn('GTM is disabled because VITE_GTAG_ID is missing or invalid.')
  }

  initSentry(app, router)

  app.use(VueDragscroll)

  app.use(VueAwesomePaginate)
  app.use(Vue3Lottie, { name: 'Vue3Lottie' })

  app.mount('#app')
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true })
} else {
  bootstrap()
}

declare global {
  interface String {
    toPersianDigits(): string
  }
}

String.prototype.toPersianDigits = function (): string {
  const id = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
  return this.replace(/[0-9]/g, (w) => id[+w])
}
